import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { authedAtTimestampAtom } from '../../auth/state';
import { useActiveBrand } from '../../components/branding/useActiveBrand';
import { logger } from '../../datadog';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { sessionInfoAtom } from '../../state';
import { Statsig } from '../../statsig/StatsigProvider';
import { ExperimentLayers } from '../../statsig/experiments';
import {
  VisitorState,
  clientRequiresSafetyGuardsAtom,
  experiencedHardBlockThisSessionAtom,
  hasEvaluatedSoftBlockEligibilityAtom,
  isPayorExcludedFromSoftBlockAtom,
  isSoftBlockResolvedAtom,
  staleSoftBlockMeasureTypesAtom,
  visitorNameAtom,
  visitorStateAtom,
} from '../state';
import { setDefaultWaitingRoomFaviconAndTitle } from '../waiting-room/metadata';

const [softBlockExperimentName, { IsEnabled: softBlockIsEnabledParam }] = ExperimentLayers.SoftBlock;

export function useEnterWaitingRoomCallback() {
  const activeBrand = useActiveBrand();
  const { patientShortId, providerShortId } = useParams();
  const { userInputted } = useTrackEvent();
  const setVisitorName = useSetAtom(visitorNameAtom);
  const setVisitorState = useSetAtom(visitorStateAtom);
  const getAuthedAtTimestamp = useAtomCallbackMemoized((get) => get(authedAtTimestampAtom), []);
  const getSoftBlockInfo = useAtomCallbackMemoized((get) => get(sessionInfoAtom)?.softBlock, []);
  const getIsSoftBlockResolved = useAtomCallbackMemoized((get) => get(isSoftBlockResolvedAtom), []);
  const getStaleMeasureTypes = useAtomCallbackMemoized((get) => get(staleSoftBlockMeasureTypesAtom), []);
  const getExperiencedHardBlockThisSession = useAtomCallbackMemoized(
    (get) => get(experiencedHardBlockThisSessionAtom),
    [],
  );
  const getClientRequiresSafetyGuards = useAtomCallbackMemoized((get) => get(clientRequiresSafetyGuardsAtom), []);
  const getIsPayorExcludedFromSoftBlock = useAtomCallbackMemoized((get) => get(isPayorExcludedFromSoftBlockAtom), []);
  // Read-and-set the once-per-session guard atomically so the eligibility event
  // fires only on the first (determining) routing evaluation.
  const markEligibilityEvaluatedOnce = useAtomCallbackMemoized((get, set) => {
    if (get(hasEvaluatedSoftBlockEligibilityAtom)) return false;
    set(hasEvaluatedSoftBlockEligibilityAtom, true);
    return true;
  }, []);

  return useCallback(
    (visitorName: string) => {
      const isAuthenticated = getAuthedAtTimestamp() !== null;
      const isServerEligible = getSoftBlockInfo()?.isServerEligible ?? false;
      const staleMeasureTypes = getStaleMeasureTypes() ?? [];
      const experiencedHardBlock = getExperiencedHardBlockThisSession();
      const wasAlreadyResolved = getIsSoftBlockResolved();
      // Under-13 clients (COPPA) require safety guards and must never be
      // soft-blocked. Treat an unresolved value as "requires guards" so we err on
      // the side of excluding a possible minor under 13.
      const isWithinAgeThreshold = !(getClientRequiresSafetyGuards() ?? true);
      // Excluded-payor clients (e.g. KPNC) must never be soft-blocked. Treat an
      // unresolved value as "excluded" so we err on the side of not soft-blocking
      // when the payor exclusion is unknown.
      const isPayorExcluded = getIsPayorExcludedFromSoftBlock() ?? true;
      // Fail open: an unresolved (undefined) or empty stale-measures result keeps
      // the client out of the soft block. We never show it without stale measures.
      const hasStaleMeasures = staleMeasureTypes.length > 0;
      const isEligibleForSoftBlock =
        isAuthenticated &&
        isServerEligible &&
        hasStaleMeasures &&
        !experiencedHardBlock &&
        isWithinAgeThreshold &&
        !isPayorExcluded;

      // Both the eligibility event and the experiment bucketing below must run
      // only on the first (determining) routing evaluation, so read-and-consume
      // the once-per-session guard up front.
      const isDeterminingEvaluation = markEligibilityEvaluatedOnce();

      // Bucket into the soft-block experiment only for fully-eligible clients.
      // Reading the layer param logs a Statsig exposure, so gating it behind full
      // eligibility keeps the experiment population isolated to eligible clients
      // and logs at most one exposure per client. Control — and both arms of the
      // A/A — resolve `false`, which keeps the normal waiting-room experience.
      const softBlockExperimentEnabled =
        isDeterminingEvaluation && isEligibleForSoftBlock
          ? (Statsig.getLayer(softBlockExperimentName).get<boolean>(softBlockIsEnabledParam, false) ?? false)
          : false;

      const shouldSoftBlock = softBlockExperimentEnabled && !wasAlreadyResolved;

      if (isDeterminingEvaluation) {
        userInputted(EventTypeEnum.SOFT_BLOCK_ELIGIBILITY_EVALUATED, {
          patientShortId,
          providerShortId,
          isEligible: isEligibleForSoftBlock,
          isServerEligible,
          isAuthenticated,
          hasStaleMeasures,
          staleMeasureTypes,
          experiencedHardBlock,
          isWithinAgeThreshold,
          isPayorExcluded,
          wasAlreadyResolved,
        });

        logger.info('Soft-block eligibility evaluated', {
          feature: 'soft_block',
          patientShortId,
          providerShortId,
          isEligible: isEligibleForSoftBlock,
          isServerEligible,
          isAuthenticated,
          hasStaleMeasures,
          staleMeasureTypes,
          experiencedHardBlock,
          isWithinAgeThreshold,
          isPayorExcluded,
          wasAlreadyResolved,
          experimentEnabled: softBlockExperimentEnabled,
          shouldSoftBlock,
        });
      }

      setVisitorName(visitorName);

      if (shouldSoftBlock) {
        return setVisitorState(VisitorState.PRESESSION_MEASURES);
      }

      setVisitorState(VisitorState.WAITING);
      setDefaultWaitingRoomFaviconAndTitle(activeBrand);
    },
    [
      activeBrand,
      patientShortId,
      providerShortId,
      userInputted,
      markEligibilityEvaluatedOnce,
      setVisitorName,
      setVisitorState,
      getAuthedAtTimestamp,
      getSoftBlockInfo,
      getIsSoftBlockResolved,
      getStaleMeasureTypes,
      getExperiencedHardBlockThisSession,
      getClientRequiresSafetyGuards,
      getIsPayorExcludedFromSoftBlock,
    ],
  );
}
